/** Pure file-selection & guest-detail validation logic (unit tested). No DOM, no tenant specifics. */

export const MAX_FILES = 10;
export const MAX_VIDEO_BYTES = 25 * 1024 * 1024; // 25 MB client-side cap for videos
export const MAX_CAPTION_LENGTH = 120;

export type FileKind = "image" | "heic" | "video" | "unsupported-video" | "unsupported";

/**
 * Images: any decodable image is accepted at pick time because the pipeline
 * ALWAYS re-encodes to JPEG (HEIC included) — the upload manifest only ever
 * reports image/jpeg, which the API allows. So input format here is permissive.
 */
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tif", "tiff", "avif"];
const HEIC_EXTENSIONS = ["heic", "heif"];

/**
 * Videos are NOT transcoded — they upload as-is, so the manifest reports the
 * source video type. The API allowlist is exactly video/mp4 and video/quicktime,
 * so we accept only MP4 and MOV up front and reject everything else with a clear
 * message (WI #44). Other video formats are tracked separately for that message.
 */
const VIDEO_MP4_EXTENSIONS = ["mp4"];
const VIDEO_MOV_EXTENSIONS = ["mov", "qt"];
const OTHER_VIDEO_EXTENSIONS = ["webm", "3gp", "3gpp", "mkv", "m4v", "avi", "wmv", "flv", "mpg", "mpeg"];

/** API-accepted content types (must match the backend allowlist exactly). */
export const ALLOWED_IMAGE_CONTENT_TYPE = "image/jpeg";
export const ALLOWED_VIDEO_CONTENT_TYPES = ["video/mp4", "video/quicktime"] as const;

/** Phone pattern mirrors the API: 7–24 chars of digits, spaces, and ( ) - . + (WI #50). */
const PHONE_PATTERN = /^[\d ()+.-]{7,24}$/;
const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot < 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}

/**
 * Classify a picked file by MIME type, falling back to extension because
 * mobile browsers sometimes hand over files with an empty `type`.
 * Video formats outside the API allowlist return "unsupported-video" so the
 * caller can show a format-specific message instead of a generic one.
 */
export function classifyFile(name: string, type: string): FileKind {
  const mime = (type || "").toLowerCase();
  const ext = extensionOf(name);

  if (mime === "image/heic" || mime === "image/heif" || HEIC_EXTENSIONS.includes(ext)) {
    return "heic";
  }
  if (mime === "image/svg+xml" || ext === "svg") {
    // SVG is explicitly disallowed (stored-content attack surface — see SECURITY.md T9).
    return "unsupported";
  }
  if (mime.startsWith("image/")) return "image";

  // Videos: only MP4 / QuickTime are accepted (API allowlist).
  if (mime === "video/mp4" || mime === "video/quicktime") return "video";
  if (mime.startsWith("video/")) return "unsupported-video";

  // Extension fallbacks (empty MIME from mobile pickers).
  if (IMAGE_EXTENSIONS.includes(ext)) return "image";
  if (VIDEO_MP4_EXTENSIONS.includes(ext) || VIDEO_MOV_EXTENSIONS.includes(ext)) return "video";
  if (OTHER_VIDEO_EXTENSIONS.includes(ext)) return "unsupported-video";

  return "unsupported";
}

/**
 * Resolve the exact content type to report for an accepted video so the upload
 * manifest matches the API allowlist. Fixes the bug where a .mov with an empty
 * MIME type was mislabeled video/mp4 (WI #44): MOV → video/quicktime.
 */
export function videoContentType(name: string, type: string): "video/mp4" | "video/quicktime" {
  const mime = (type || "").toLowerCase();
  if (mime === "video/mp4") return "video/mp4";
  if (mime === "video/quicktime") return "video/quicktime";
  const ext = extensionOf(name);
  if (VIDEO_MOV_EXTENSIONS.includes(ext)) return "video/quicktime";
  return "video/mp4";
}

export interface CandidateFile {
  name: string;
  type: string;
  size: number;
}

export interface AcceptedFile<T extends CandidateFile = CandidateFile> {
  file: T;
  kind: Exclude<FileKind, "unsupported" | "unsupported-video">;
}

export interface RejectedFile<T extends CandidateFile = CandidateFile> {
  file: T;
  reason: string;
}

export interface SelectionResult<T extends CandidateFile = CandidateFile> {
  accepted: AcceptedFile<T>[];
  rejected: RejectedFile<T>[];
}

function friendlySize(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

/**
 * Validate newly picked files against what is already selected.
 * Enforces: supported types only, video size cap, MAX_FILES total.
 */
export function validateSelection<T extends CandidateFile>(
  existingCount: number,
  picked: T[]
): SelectionResult<T> {
  const accepted: AcceptedFile<T>[] = [];
  const rejected: RejectedFile<T>[] = [];
  let count = existingCount;

  for (const file of picked) {
    const kind = classifyFile(file.name, file.type);
    if (kind === "unsupported-video") {
      rejected.push({
        file,
        reason: `“${file.name}” is a video format we can’t accept — MP4 or MOV, please.`
      });
      continue;
    }
    if (kind === "unsupported") {
      rejected.push({ file, reason: `“${file.name}” isn’t a photo or video we can accept.` });
      continue;
    }
    if (kind === "video" && file.size > MAX_VIDEO_BYTES) {
      rejected.push({
        file,
        reason: `“${file.name}” is ${friendlySize(file.size)} — videos need to be under ${friendlySize(MAX_VIDEO_BYTES)}. A shorter clip will do the trick!`
      });
      continue;
    }
    if (file.size === 0) {
      rejected.push({ file, reason: `“${file.name}” looks empty — please try picking it again.` });
      continue;
    }
    if (count >= MAX_FILES) {
      rejected.push({
        file,
        reason: `You can share up to ${MAX_FILES} photos or videos at a time — “${file.name}” didn’t make the cut.`
      });
      continue;
    }
    accepted.push({ file, kind });
    count += 1;
  }

  return { accepted, rejected };
}

/** Clamp and clean a caption for submission. */
export function normalizeCaption(raw: string): string {
  return raw.replace(/\s+/g, " ").trim().slice(0, MAX_CAPTION_LENGTH);
}

export function isValidEmail(email: string): boolean {
  return EMAIL_PATTERN.test(email.trim());
}

export function isValidPhone(phone: string): boolean {
  return PHONE_PATTERN.test(phone.trim());
}

export interface GuestSubmission {
  name: string;
  email: string;
  phone: string;
  consent: boolean;
  fileCount: number;
}

/**
 * Validate everything the API will reject, BEFORE any blob upload starts, so a
 * guest never uploads and then hits a wall (WI #44/#47/#50). Returns an ordered
 * list of friendly problem messages; empty means good to go.
 */
export function validateGuestSubmission(s: GuestSubmission): string[] {
  const problems: string[] = [];
  const email = s.email.trim();
  const phone = s.phone.trim();

  if (!s.name.trim()) {
    problems.push("Please tell us your name — it appears with your photos.");
  }
  if (s.fileCount === 0) {
    problems.push("Add at least one photo or video to share.");
  }
  if (email && !isValidEmail(email)) {
    problems.push("That email doesn’t look quite right — mind checking it?");
  }
  if (phone && !isValidPhone(phone)) {
    problems.push("That phone number doesn’t look right — digits, spaces and ( ) + - . only, please.");
  }
  // Consent requires a contact channel (API rejects consent with no email/phone) — WI #47.
  if (s.consent && !email && !phone) {
    problems.push("To hear about future stays and offers, add your email or phone — or untick the marketing box.");
  }
  return problems;
}
